import Link from "next/link";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import {
  recommendationActionedEvent,
  recommendationViewedEvent,
} from "@/lib/companion/analytics-events";
import type { CompanionRecommendationModel } from "../recommendation-model";
import { sportIsBeta } from "@/lib/conditions/sport-meta";
import { SportBetaBadge } from "@/app/_components/conditions/SportBetaBadge";
import { ScoreRing } from "@/app/_components/verdict/ScoreRing";
import {
  CompanionTrackedLink,
  CompanionViewAnalytics,
} from "./CompanionAnalytics";

export type CompanionRecommendationCardProps = {
  model: CompanionRecommendationModel;
};

export function CompanionRecommendationCard({
  model,
}: CompanionRecommendationCardProps) {
  const isSetup = model.state === "setup";
  const actionEvent = (
    actionKind: CompanionRecommendationModel["primaryAction"]["kind"],
  ) =>
    recommendationActionedEvent({
      actionKind,
      decisionAction: model.decisionAction,
      confidence: model.confidenceLabel,
      freshness: model.freshnessLabel,
      emptyReason: model.emptyReason,
    });

  return (
    <section
      aria-labelledby="recommendation-heading"
      className="overflow-hidden rounded-lg bg-navy text-white shadow-[0_20px_60px_rgba(22,35,59,0.18)]"
    >
      <CompanionViewAnalytics
        event={recommendationViewedEvent({
          decisionAction: model.decisionAction,
          confidence: model.confidenceLabel,
          freshness: model.freshnessLabel,
          emptyReason: model.emptyReason,
        })}
      />
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1.35fr)_minmax(240px,0.65fr)]">
        <div className="p-3 lg:col-start-1 lg:row-start-1 lg:p-7">
          <p className="text-[10px] font-semibold uppercase tracking-[1.6px] text-teal-light lg:text-[11px] lg:tracking-[1.8px]">
            {model.label}
          </p>
          <h2
            id="recommendation-heading"
            className="mt-1 font-serif text-[26px] leading-[0.98] text-white lg:mt-2 lg:text-[42px] lg:leading-[1.02]"
          >
            {model.verdict}
          </h2>

          {model.sportLabel || model.placeName ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] font-semibold text-white/82 lg:mt-3 lg:text-[14px]">
              <p>
                {[model.sportLabel, model.placeName, model.placeRegion]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {model.sportKey && sportIsBeta(model.sportKey)
                ? <SportBetaBadge emphasised />
                : null}
            </div>
          ) : null}

          {model.callTakeover ? (
            <div className="mt-2 flex items-start gap-2 rounded-sm border border-gold/45 bg-gold/10 px-2.5 py-2 lg:mt-5 lg:block lg:p-4">
              <p className="shrink-0 pt-0.5 text-[8px] font-semibold uppercase tracking-[1.2px] text-gold-light lg:pt-0 lg:text-[11px] lg:tracking-[1.5px]">
                Your Call alert
              </p>
              <p className="font-serif text-[15px] leading-tight text-white lg:mt-2 lg:text-[24px]">
                {model.callTakeover.title}
              </p>
              {model.callTakeover.href ? (
                <Link href={model.callTakeover.href} className="inline-flex min-h-[44px] min-w-[44px] items-center text-[12px] font-semibold text-gold-light underline">
                  Open the Call
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>

        <RecommendationTrust model={model} />

        <div className="hidden px-7 pb-2 lg:col-start-1 lg:row-start-2 lg:block">
          <RecommendationSupportingDetails model={model} includeTrust={false} />
        </div>

        <div className="relative grid grid-cols-[repeat(2,minmax(0,auto))] justify-start gap-2 px-3 py-2 pr-[52px] lg:col-start-1 lg:row-start-3 lg:flex lg:gap-3 lg:px-7 lg:pb-7 lg:pt-5">
          <CompanionTrackedLink
            href={model.primaryAction.href}
            event={actionEvent(model.primaryAction.kind)}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-teal px-3 py-2 text-[11px] font-semibold text-white no-underline hover:bg-teal-dark lg:px-5 lg:py-2.5 lg:text-[13px]"
          >
            {model.primaryAction.label}
          </CompanionTrackedLink>
          {model.secondaryAction ? (
            <CompanionTrackedLink
              href={model.secondaryAction.href}
              event={actionEvent(model.secondaryAction.kind)}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-white/22 px-3 py-2 text-[11px] font-semibold text-white no-underline hover:border-teal-light hover:text-teal-light lg:px-5 lg:py-2.5 lg:text-[13px]"
            >
              {model.secondaryAction.label}
            </CompanionTrackedLink>
          ) : null}
          <details className="contents lg:hidden">
            <summary className="absolute right-1 top-2 inline-flex h-[44px] w-[44px] cursor-pointer list-none items-center justify-center rounded-full border border-white/22 text-teal-light marker:hidden">
              <span className="sr-only">Why this recommendation</span>
              <span aria-hidden="true" className="text-[18px] text-white/72">
                +
              </span>
            </summary>
            <div className="col-span-full -mr-10 border-t border-white/12 pb-2 pt-3">
              <RecommendationSupportingDetails model={model} includeTrust={!isSetup} />
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}

function RecommendationTrust({
  model,
}: {
  model: CompanionRecommendationModel;
}) {
  const isSetup = model.state === "setup";

  return (
    <div className="border-t border-white/12 bg-white/[0.05] px-3 py-2 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:border-l lg:border-t-0 lg:p-7">
      {isSetup ? null : (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[1.5px] text-white/80">
          {model.decisionLabel}
        </p>
      )}
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 lg:block">
        {isSetup ? (
          <div data-testid="recommendation-score" className="lg:mt-3">
            <ScoreRing score={null} size={88} numberColor="#FFFFFF" />
          </div>
        ) : model.score === null ? (
          <p
            data-testid="recommendation-score"
            className="font-serif text-[20px] leading-none text-white lg:mt-3"
          >
            No score
          </p>
        ) : (
          <div data-testid="recommendation-score" className="lg:mt-3">
            <ScoreRing
              score={scoreOutOf100(model.score)}
              size={88}
              numberColor="#FFFFFF"
              caption={model.decisionAction === "go-now" ? "GO" : null}
            />
          </div>
        )}

        {isSetup ? (
          <p className="text-[11px] leading-snug text-white/70 lg:mt-5 lg:text-[12.5px] lg:leading-relaxed">
            Your GO score lands here after your first save.
          </p>
        ) : (
          <div className="rounded-sm border border-terracotta/55 bg-terracotta/10 px-2.5 py-2 lg:mt-5 lg:p-3">
            <p className="text-[9px] font-semibold uppercase tracking-[1.2px] text-white lg:text-[10px] lg:tracking-[1.4px]">
              Safety
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-white/82 lg:mt-1 lg:text-[12.5px] lg:leading-relaxed">
              {model.safetyLine}
            </p>
          </div>
        )}
      </div>

      {isSetup ? null : (
        <div className="mt-2 text-[12px] lg:hidden">
          {model.windowLine ? <p className="font-semibold text-gold-light">{model.windowLine}</p> : null}
          <p className="mt-1 text-white/80">{model.freshnessLabel}</p>
        </div>
      )}
      {isSetup ? null : (
        <dl className="mt-5 hidden space-y-4 lg:block">
          <TrustDetails model={model} />
        </dl>
      )}
    </div>
  );
}

function RecommendationSupportingDetails({
  model,
  includeTrust,
}: {
  model: CompanionRecommendationModel;
  includeTrust: boolean;
}) {
  const suppressGenericSetupReason =
    model.state === "setup" && model.primaryAction.label === "Save a place";

  return (
    <>
      {suppressGenericSetupReason ? null : (
        <p className="mt-2 max-w-[62ch] text-[12px] leading-relaxed text-white/72 lg:mt-4 lg:text-[14px]">
          {model.reason}
        </p>
      )}
      {model.windowLine ? (
        <p className="mt-2 text-[12px] font-semibold text-gold-light lg:mt-4 lg:text-[13px]">
          {model.windowLine}
        </p>
      ) : null}
      {model.fitLine ? (
        <p className="mt-1.5 text-[12px] leading-relaxed text-white/72 lg:mt-2 lg:text-[13px]">
          {model.fitLine}
        </p>
      ) : null}
      {model.nextUsefulLine ? (
        <p className="mt-1.5 text-[12px] leading-relaxed text-white/62 lg:mt-2 lg:text-[13px]">
          {model.nextUsefulLine}
        </p>
      ) : null}

      {includeTrust ? (
        <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-white/12 pt-3">
          <TrustDetails model={model} />
        </dl>
      ) : null}

      {model.alternatives.length > 0 ? (
        <div className="mt-3 space-y-1 border-t border-white/12 pt-2 lg:mt-4 lg:space-y-1.5 lg:pt-3">
          {model.alternatives.map((alternative) => (
            <Link
              key={`${alternative.kind}:${alternative.href}`}
              href={alternative.href}
              className="block min-h-[44px] py-2 text-[12px] font-semibold leading-relaxed text-white/70 no-underline hover:text-teal-light lg:text-[12.5px]"
            >
              {alternative.label}
            </Link>
          ))}
        </div>
      ) : null}
    </>
  );
}

function TrustDetails({ model }: { model: CompanionRecommendationModel }) {
  return (
    <>
      <div>
        <dt className="text-[9px] font-semibold uppercase tracking-[1.2px] text-white/52 lg:text-[10px] lg:tracking-[1.4px]">
          Confidence
        </dt>
        <dd className="mt-0.5 text-[11px] font-semibold text-white/82 lg:mt-1 lg:text-[13px]">
          {model.confidenceLabel}
        </dd>
      </div>
      <div>
        <dt className="text-[9px] font-semibold uppercase tracking-[1.2px] text-white/52 lg:text-[10px] lg:tracking-[1.4px]">
          Freshness
        </dt>
        <dd className="mt-0.5 text-[11px] font-semibold text-white/82 lg:mt-1 lg:text-[13px]">
          {model.freshnessLabel}
        </dd>
      </div>
      {model.confirmedLabel ? (
        <div>
          <dt className="text-[9px] font-semibold uppercase tracking-[1.2px] text-white/52 lg:text-[10px] lg:tracking-[1.4px]">
            Ground truth
          </dt>
          <dd className="mt-0.5 text-[11px] font-semibold text-white/82 lg:mt-1 lg:text-[13px]">
            {model.confirmedLabel}
          </dd>
        </div>
      ) : null}
    </>
  );
}
